import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/LanguageContext";
import { getLocalizedPath } from "@/lib/i18nRoutes";

const promptDefaults = {
  en: { eyebrow: "Free win unlocked", primary: "See Premium", secondary: "Free briefing" },
  he: { eyebrow: "הצלחה חינמית נפתחה", primary: "ראו פרימיום", secondary: "תדריך חינמי" },
  es: { eyebrow: "Ventaja gratis desbloqueada", primary: "Ver Premium", secondary: "Briefing gratis" },
  it: { eyebrow: "Vantaggio gratuito sbloccato", primary: "Vedi Premium", secondary: "Briefing gratuito" },
  fr: { eyebrow: "Avantage gratuit débloqué", primary: "Voir Premium", secondary: "Briefing gratuit" },
  de: { eyebrow: "Kostenloser Vorteil freigeschaltet", primary: "Premium ansehen", secondary: "Kostenloses Briefing" },
  pt: { eyebrow: "Ganho gratuito desbloqueado", primary: "Ver Premium", secondary: "Briefing grátis" },
  ar: { eyebrow: "ميزة مجانية مفتوحة", primary: "عرض بريميوم", secondary: "موجز مجاني" },
  el: { eyebrow: "Δωρεάν όφελος ξεκλειδώθηκε", primary: "Δείτε Premium", secondary: "Δωρεάν ενημέρωση" },
  ru: { eyebrow: "Бесплатная польза открыта", primary: "Смотреть Premium", secondary: "Бесплатный брифинг" },
  tr: { eyebrow: "Ücretsiz avantaj açıldı", primary: "Premium’u gör", secondary: "Ücretsiz brifing" },
  zh: { eyebrow: "免费价值已解锁", primary: "查看高级版", secondary: "免费简报" },
};

export default function ContextualUpgradePrompt({ eyebrow, title, text, bullets = [], primaryLabel, secondaryLabel }) {
  const { lang: routeLang } = useParams();
  const { language } = useLanguage();
  const lang = routeLang || language || "en";
  const copy = promptDefaults[lang] || promptDefaults.en;
  const premiumPath = getLocalizedPath("premium", lang);
  const briefingPath = getLocalizedPath("dailyBriefing", lang);
  const displayEyebrow = eyebrow || copy.eyebrow;
  const displayPrimaryLabel = primaryLabel || copy.primary;
  const displaySecondaryLabel = secondaryLabel || copy.secondary;

  return (
    <section className="rounded-[2rem] border border-primary/20 bg-card p-5 shadow-xl shadow-ocean/10 md:p-7">
      <p className="text-xs font-black uppercase tracking-[0.2em] text-primary">{displayEyebrow}</p>
      <div className="mt-3 grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
        <div>
          <h2 className="font-heading text-2xl font-black md:text-3xl">{title}</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground md:text-base md:leading-7">{text}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {bullets.map((item) => <span key={item} className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary"><CheckCircle2 className="h-3.5 w-3.5" />{item}</span>)}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
          <Button asChild><Link to={premiumPath}>{displayPrimaryLabel} <ArrowRight className="h-4 w-4" /></Link></Button>
          <Button asChild variant="outline"><Link to={briefingPath}><ShieldCheck className="h-4 w-4" /> {displaySecondaryLabel}</Link></Button>
        </div>
      </div>
    </section>
  );
}